import BaseAuthProvider from './BaseAuthProvider'
import {AuthProviders} from '@/domain/login/constant/authProviders'

/**
 * Facebook OAuth authentication provider
 */
export default class FacebookAuthProvider extends BaseAuthProvider {
    /**
     * Load Facebook SDK
     */
    async loadSDK() {
        const facebookClientId = process.env.VUE_APP_FACEBOOK_APP_ID

        return new Promise((resolve) => {
            window.fbAsyncInit = function () {
                window.FB.init({
                    appId: facebookClientId,
                    cookie: true,
                    xfbml: true,
                    version: 'v15.0'
                })
                resolve()
            }

            const script = document.createElement("script")
            script.src = "https://connect.facebook.net/en_US/sdk.js"
            script.async = true
            script.defer = true
            document.body.appendChild(script)
        })
    }

    /**
     * Start Facebook login flow and get access token
     * @returns {Promise<string>} Access token
     */
    async startLogin() {
        const params = {scope: 'email'}

        return new Promise((resolve, reject) => {
            window.FB.login(response => {
                if (response && response.authResponse) {
                    // Verify user has email permission
                    window.FB.api(`/${response.authResponse.userID}?fields=email`, params, userResponse => {
                        if (userResponse && userResponse.email) {
                            resolve(response.authResponse.accessToken)
                        } else {
                            // Email permission denied, revoke app permissions
                            window.FB.api(`/${response.authResponse.userID}/permissions/`, 'delete', () => {
                                reject(new Error('Facebook email permission required'))
                            })
                        }
                    })
                } else {
                    reject(new Error('Facebook login cancelled'))
                }
            }, params)
        })
    }
    /**
     * Authenticate with Facebook OAuth token
     * @param {Object} credentials - {token, language?, allowRegister?}
     * @returns {Promise<Object>} {token: access_token, type: token_type, jwt}
     */
    async authenticate(credentials) {
        const {token, language, allowRegister = false} = credentials

        const params = {
            backend: 'facebook',
            token,
            grant_type: 'convert_token',
            client_id: process.env.VUE_APP_OAUTH_CLIENT_ID,
            is_dashboard: true
        }

        if (language) {
            params.language = language
        }

        if (allowRegister) {
            params.allow_register = true
        }

        const result = await this.service.convertAuthToken(params)

        return {
            token: result.access_token,
            type: result.token_type,
            jwt: result.jwt
        }
    }

    async logout(tokenData) {
        // Facebook auth doesn't need server-side logout
    }

    supportsRefresh() {
        return false
    }

    getMethod() {
        return AuthProviders.FACEBOOK
    }
}
